import React, {useEffect, useRef, useState} from "react";

import {Box, Button, Typography} from "@mui/material";
import {makeStyles} from "tss-react/mui";

import MaskSelectorToolStyles from "../MaskSelectorToolStyles";


import ears_visible from "./ears_visible.svg"
import eye_level from "./eye_level.svg"
import well_lit from "./well_lit.svg"

import * as dTokens from "ui-common/components/design-tokens";

import useSound from 'use-sound'
import testTone from "./scancomplete.wav"

import volume_animation from "../img/lottie/volume_animation_v2.json"

import BackNextNavigation from "../BackNextNavigation";
import {useTranslation} from "react-i18next";
import Lottie from "lottie-react";

import SoundPlayer from "./SoundPlayer";

const ScanInstructions = ( { handleNext, handlePrevious = undefined, instruction } ) => {
    const {classes} = MaskSelectorToolStyles();
    const [isPlaying, setIsPlaying] = useState(false);
    const [audioKey, setAudioKey] = useState(undefined);
    const volumeAnimationRef = useRef();

    const [playSound, {sound}] = useSound(testTone);

    const tenPercentTransparency = "1A";

    const bottomMargin = instruction === "volume" ? "62px" : "86px";

    const ScanInstructionsStyles = makeStyles()(() => ({
        preHeader: {
            fontSize: 13,
            fontWeight: 400,
            lineHeight: "19.5px"
        },

        volumeButton: {
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            borderRadius: '30px',
            border: `1px solid ${dTokens.res_brand_color_005}`,
            background: '#FFF',
            fontSize: '16px',
            fontWeight: 600,
            color: dTokens.res_brand_color_005,
            lineHeight: '20px',
            letterSpacing: '0.1px',
            textAlign: 'center',
            cursor: 'pointer',
            width: '204px',
            height: '44px',
        },

        instructionHeader: {
            color: dTokens.res_grey_900,
            textAlign: "center",
            lineHeight: "25px"
        },

        soundPlayingAnimation: {
            "@media only screen and (max-height: 599px)": {
                height: "15svh", // 30% of 50svh container height
                marginBottom: "5svh" // 10% of 50svh container height
            },
            "@media only screen and (min-height: 600px)": {
                height: "90px",
                marginBottom: "32px"
            }
        },

        activeVolumeButton: {
            backgroundColor: `${dTokens.res_brand_color_005}${tenPercentTransparency} !important`
        },

        detail: {
            fontSize: 16,
            fontWeight: 400
        },

        topMargin: {
            "@media only screen and (max-height: 599px)": {
                marginTop: "24px"
            },
            "@media only screen and (min-height: 600px)": {
                marginTop: "40px"
            }
        },

        animationButtonContainer: {
            "@media only screen and (max-height: 599px)": {
                // Vertical padding here is 50svh (entire height of container) minus 20svh (animation and margin bottom) 
                // minus 44px (button height) all divided by 2.
                padding: "calc((30svh - 44px) / 2) 0"
            },
            "@media only screen and (min-height: 600px)": {
                // Same as above but it's not responsive when the screen height is at least 600px.
                padding: "67px 0"
            }
        }

    }));

    const {classes: scanInstructionsClasses} = ScanInstructionsStyles();

    const headingRef = useRef(null);

    useEffect(() => {
        if(headingRef?.current) {
            headingRef.current.focus();
        }
    });

    const playVolumeTest = () => {
        playSound();
        setIsPlaying(true);

        // The play function will cause problems if we do it right now so do it later.
        setTimeout(() => {
            volumeAnimationRef.current?.play();
        }, 0);
    };

    useEffect(() => {
        if (sound) {
            sound.on("end", () => {
                setIsPlaying(false);
                volumeAnimationRef.current?.stop();
            });
        }
    }, [sound]);

    const { t } = useTranslation("pages.MaskSelector.nextGenScan.ScanInstructions");
    const { t: tVolume } = useTranslation("pages.MaskSelector.nextGenScan.VolumePage");

    let image = undefined;
    let headerText = "";
    let instructionText = "";
    let ExtraComponent = undefined;

    switch(instruction) {
        case "ears":
            image = ears_visible;
            headerText = t("earsVisibleHeader");
            instructionText = t("earsVisibleDescription");
            break;

        case "well_lit":
            image = well_lit;
            headerText = t("wellLitHeader");
            instructionText = t("wellLitDescription");
            break;

        case "level":
            image = eye_level;
            headerText = t("levelHeader");
            instructionText = t("levelDescription");
            break;

        case "volume":
            headerText = tVolume("silentModeOff");
            instructionText = t("volumeDescription");
            ExtraComponent = () => (
                <Box flexDirection="column" className={`${classes.responsive_image_50_percent_height} ${scanInstructionsClasses.animationButtonContainer}`}>
                    <Box className={classes.centeredBox}>
                        <Lottie
                            lottieRef={volumeAnimationRef}
                            autoplay={false}
                            loop={true}
                            animationData={volume_animation}
                            data-testid="volumeAnimation"
                            className={scanInstructionsClasses.soundPlayingAnimation} />
                    </Box>
                    <Box className={classes.centeredBox} >
                        <Button
                            variant="outlined"
                            className={
                                isPlaying ? `${scanInstructionsClasses.volumeButton} ${scanInstructionsClasses.activeVolumeButton}` 
                                : `${scanInstructionsClasses.volumeButton}`
                            }
                            onClick={playVolumeTest}
                            >
                            {t("volumeButtonLabel")}
                        </Button>
                    </Box>
                </Box>
                );
            break;
    }

    useEffect(() => {
        if (instruction === "ears") {
            setAudioKey("earsVisibleComposite");
        }

        if (instruction === "well_lit") {
            setAudioKey("wellLitComposite");
        }

        if (instruction === "level") {
            setAudioKey("levelComposite");
        }
    }, [instruction]);


    const buttonNavigationHeight = "70px";

    return (
        <Box className={classes.contentOuterFlexBox}>
            <Box
                className={ `${classes.mainBox} ${classes.instructionMainBox} ${classes.contentInnerOverFlowBox}` }
                mb={bottomMargin}
            >
                <SoundPlayer soundId={audioKey} />
                <Box sx={{height: `calc(100% - ${buttonNavigationHeight})`}}>

                    {image && (
                        <Box
                            className={ `${classes.instructionBox} ${classes.centeredBox} ${classes.responsive_image_50_percent_height}` }  
                            data-testid={`scan-instructions-image-${instruction}`}>
                            <img className={classes.responsive_image_50_percent_height} alt="" src={image} style={{top: '177px', left: '39px'}} />
                        </Box>
                    )}

                    {ExtraComponent && (
                        <Box className={classes.centeredBox}>
                            <ExtraComponent />
                        </Box>
                    )}

                    <Box className={ `${classes.centeredBox}` } mb="16px">
                        <Typography className={`${classes.res_heading5} ${classes.noOutline} ${scanInstructionsClasses.instructionHeader}`} tabIndex="0" aria-label={headerText} ref={headingRef}>{headerText}</Typography>
                    </Box>

                    <Box className={classes.centeredBox}>
                        <Typography className={`${scanInstructionsClasses.detail} ${classes.centeredText} ${classes.instruction} ${classes.noOutline}`} tabIndex="0" aria-label={instructionText}>{instructionText}</Typography>
                    </Box>
                </Box>

                {/* Need to add padding so that the text can scroll past the transparent bit */}
                <Box mt="40px"></Box>

            </Box>
            <BackNextNavigation
                handleNext={handleNext}
                handlePrevious={handlePrevious}
                containerClassName={classes.navButtonsBox}
            />
        </Box>
    )
}

export default ScanInstructions;
